<script lang="ts">
import { useCountdown } from '@/plugins/useCountdown'
import type { DateTime } from 'luxon'
import { type PropType, defineComponent } from 'vue'

/**
 * A countdown timer display.
 *
 * The datetime much be a Luxon object.
 *
 * If the future date is greater than 30 days
 * a date will be displayed - in the "long" format -
 * instead of a day/hour/min/sec countdown
 * (as per the `useCountdown()` mixin).
 */
export default defineComponent({
  inheritAttrs: false,
  props: {
    /**
     * The ending datetime to countdown to.
     * Must be of type (Luxon) DateTime.
     */
    datetime: {
      type: Object as PropType<DateTime>,
      required: true,
    },

    /**
     * A callback function to call when the timer
     * ends.
     */
    endCallback: {
      type: Function,
    },

    /**
     * Classes to add to the time text.
     */
    timeClass: {
      type: String,
      default: '',
    },

    /**
     * Classes to add to the unit text.
     */
    unitClass: {
      type: String,
    },

    /**
     * If true will ignore the hiding of the
     * day or seconds seconds as per the `useCountdown()`
     * mixin.
     */
    showAllUnits: {
      type: Boolean,
      default: false,
    },
    /**
     * If true the full time unit word will be displayed.
     * d = day
     * h = hour
     * m = min
     * s = sec
     */
    showFullUnits: {
      type: Boolean,
      default: false,
    },
    /**
     * If true will ignore the hiding of the
     * day or seconds seconds as per the `useCountdown()`
     * mixin.
     */
    countdownThreshold: {
      type: Number,
      default: 30,
    },
    /**
     * Set countdown text if there is any
     */
    countdownText: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      countdown: useCountdown(this.endCallback, this.countdownThreshold),
    }
  },
  computed: {
    /**
     * Only display the remaining seconds if
     * there's less then 1 day remaining.
     *
     * @returns boolean
     */
    displaySeconds(): boolean {
      return this.countdown.remainingDateTime.days <= 0
    },
  },
  mounted() {
    this.countdown.start(this.datetime)
  },
})
</script>

<template>
  <div class="flex flex-wrap justify-evenly gap-1 text-center">
    <slot :countdown />

    <time
      v-if="countdown.showAsDate"
      :datetime="datetime.toString()"
      v-bind="$attrs"
      class="flex items-center justify-center gap-1 leading-none will-change-contents"
      :class="timeClass"
    >
      <template v-if="countdownText">
        {{ countdownText }}
      </template>
      <div v-else class="font-bold" :class="timeClass">
        {{ $d(datetime.toJSDate(), 'long') }}
      </div>
    </time>

    <time
      v-if="!countdown.showAsDate"
      :datetime="datetime.toString()"
      v-bind="$attrs"
      class="flex justify-evenly gap-1 will-change-contents"
    >
      <p
        v-if="
          (showAllUnits && countdown.remainingDateTime.days > 0) ||
          !displaySeconds
        "
        class="segment"
        :class="{ 'full-unit': showFullUnits }"
      >
        <span class="time" :class="timeClass">
          <slot name="day" :day="countdown.remainingDateTimeFormatted.days">
            {{ countdown.remainingDateTimeFormatted.days }}
          </slot>
        </span>
        <span class="unit" :class="unitClass">
          <slot
            name="day-unit"
            :day-unit="
              $t(
                showFullUnits ? 'countdown.day' : 'countdown.d',
                countdown.remainingDateTime.days
              )
            "
          >
            <template v-if="showFullUnits">
              {{ $t('countdown.day', countdown.remainingDateTime.days) }}
            </template>

            <template v-if="!showFullUnits">
              {{ $t('countdown.d', countdown.remainingDateTime.days) }}
            </template>
          </slot>
        </span>
      </p>

      <p class="segment" :class="{ 'full-unit': showFullUnits }">
        <span class="time" :class="timeClass">
          <slot name="hour" :hour="countdown.remainingDateTimeFormatted.hours">
            {{ countdown.remainingDateTimeFormatted.hours }}
          </slot>
        </span>
        <span class="unit" :class="unitClass">
          <slot
            name="hour-unit"
            :hour-unit="
              $t(
                showFullUnits ? 'countdown.hr' : 'countdown.h',
                countdown.remainingDateTime.hours
              )
            "
          >
            <template v-if="showFullUnits">
              {{ $t('countdown.hr', countdown.remainingDateTime.hours) }}
            </template>

            <template v-if="!showFullUnits">
              {{ $t('countdown.h', countdown.remainingDateTime.hours) }}
            </template>
          </slot>
        </span>
      </p>

      <p class="segment" :class="{ 'full-unit': showFullUnits }">
        <span class="time" :class="timeClass">
          <slot
            name="minute"
            :minute="countdown.remainingDateTimeFormatted.minutes"
          >
            {{ countdown.remainingDateTimeFormatted.minutes }}
          </slot>
        </span>
        <span class="unit" :class="unitClass">
          <slot
            name="minute-unit"
            :minute-unit="
              $t(
                showFullUnits ? 'countdown.min' : 'countdown.m',
                countdown.remainingDateTime.minutes
              )
            "
          >
            <template v-if="showFullUnits">
              {{ $t('countdown.min', countdown.remainingDateTime.minutes) }}
            </template>

            <template v-if="!showFullUnits">
              {{ $t('countdown.m', countdown.remainingDateTime.minutes) }}
            </template>
          </slot>
        </span>
      </p>

      <p
        v-if="showAllUnits || displaySeconds"
        class="segment"
        :class="{ 'full-unit': showFullUnits }"
      >
        <span class="time" :class="timeClass">
          <slot
            name="second"
            :second="countdown.remainingDateTimeFormatted.seconds"
          >
            {{ countdown.remainingDateTimeFormatted.seconds }}
          </slot>
        </span>
        <span class="unit" :class="unitClass">
          <slot
            name="second-unit"
            :second-unit="
              $t(
                showFullUnits ? 'countdown.sec' : 'countdown.s',
                countdown.remainingDateTime.seconds
              )
            "
          >
            <template v-if="showFullUnits">
              {{ $t('countdown.sec', countdown.remainingDateTime.seconds) }}
            </template>

            <template v-if="!showFullUnits">
              {{ $t('countdown.s', countdown.remainingDateTime.seconds) }}
            </template>
          </slot>
        </span>
      </p>
    </time>
  </div>
</template>

<style lang="scss" scoped>
time,
.segment {
  @apply flex items-baseline leading-none;
}

.unit,
.time {
  @apply flex;
}

.segment {
  @apply text-center;

  @supports (font-variant-numeric: tabular-nums) {
    font-variant-numeric: tabular-nums;
  }

  @supports not (font-variant-numeric: tabular-nums) {
    min-width: calc(3ch + 0.25rem);

    &.full-unit {
      min-width: calc(6ch + 0.25rem);
    }
  }
}
</style>
